import moment from "moment";
import * as Yup from "yup";

export const defaultSteps = [
  {
    id: "bookingForm",
    fields: ["time", "gate"],
  },
  {
    id: "requested",
    fields: [],
  },
  {
    id: "cancelled",
    fields: [],
  },
];

// validations
export const schema = Yup.object().shape({
  // booking form
  location: Yup.string().required("Required"),
  hostName: Yup.string()
    .required("Required")
    .max(250, "cannotExceed250Characters"),
  hostNumber: Yup.string()
    .required("Required")
    .matches(/^[0-9]+$/, "MobileNumberEnglishDigitsOnly") // Ensures only English digits
    .matches(/^5\d{8}$/, "MobileNumber9digitsStartWith5"),
  guestName: Yup.string()
    .required("Required")
    .max(250, "cannotExceed250Characters"),
  guestNumber: Yup.string()
    .required("Required")
    .matches(/^[0-9]+$/, "MobileNumberEnglishDigitsOnly") // Ensures only English digits
    .matches(/^5\d{8}$/, "MobileNumber9digitsStartWith5"),
  arrivingDate: Yup.date()
    .typeError("invalidFormat")
    .test("is-required", "Required", (value) => !!value) // manual required
    .test(
      "is-today-or-later",
      "arrivingDateNotPast",
      (value) => !!value && moment(value).isSameOrAfter(moment().startOf("day"))
    )
    .test(
      "is-before-end-of-month",
      "arrivingDateTooLate",
      (value) =>
        !!value && moment(value).isSameOrBefore(moment().endOf("month"), "day")
    ),
  arrivingTime: Yup.date()
    .test("is-required", "Required", (value) => !!value) // manual required
    .test("is-future-time", "arrivingTimeNotPast", function (value) {
      const { arrivingDate } = this.parent;
      if (arrivingDate && moment(arrivingDate).isSame(moment(), "day")) {
        return value && moment(value).isAfter(moment());
      }
      return true; // valid if not today
    }),
  stayingTime: Yup.string().test("is-required", "Required", (value) => !!value), // manual required,
});

export interface FormFields extends Yup.InferType<typeof schema> {}

export function generateTimeOptions(
  workingFrom: { hour: number; minute: number },
  workingTo: { hour: number; minute: number }
) {
  const fromMinutes = (workingFrom.hour ?? 0) * 60 + (workingFrom.minute ?? 0);
  let toMinutes = (workingTo.hour ?? 0) * 60 + (workingTo.minute ?? 0);

  // If workingTo is less than workingFrom, assume it's the next day
  if (toMinutes < fromMinutes) {
    toMinutes += 24 * 60;
  }

  const diffInMinutes = toMinutes - fromMinutes;
  const maxHours = diffInMinutes / 60;

  const options = [];
  for (let h = 0.5; h <= maxHours; h += 0.5) {
    options.push(h);
  }

  return options;
}

export const calculateLocationPrice = ({
  startTime,
  endTime,
  service,
}: {
  startTime: Date | undefined;
  endTime: Date;
  service: any;
}) => {
  if (!startTime || !service) return 0;

  // Free service
  if (service.pricingType === "free") return 0;

  // Calculate hour difference
  const hrDiff =
    Math.abs(endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

  if (service.pricingType === "custom") {
    const startHours = Number(service.customStartHrs) || 0;
    const basePrice = Number(service.customPrice) || 0;
    const exceedingPrice = Number(service.customExceedingPrice) || 0;

    const additionalHours = Math.max(0, Math.ceil(hrDiff - startHours));

    // Determine exceeding price value
    const exceedingPriceValue = ["allHours", "fixed"].includes(
      service.customExceedingPriceType
    )
      ? exceedingPrice
      : additionalHours * exceedingPrice;

    return hrDiff > startHours ? basePrice + exceedingPriceValue : basePrice;
  }

  if (service.pricingType === "hourly") {
    const totalHours = Math.ceil(hrDiff);
    return totalHours * (Number(service.hourlyPrice) || 0);
  }

  if (service.pricingType === "daily") {
    const graceHours = 0.1667; // 10 minutes grace period
    const dailyRate = Number(service.dailyPrice) || 0;

    let additionalDays = 0;

    if (hrDiff < 24) {
      // Less than 1 day always counts as a full day
      additionalDays = 1;
    } else {
      // Subtract grace period before dividing into days
      const adjustedHrDiff = Math.max(0, hrDiff - graceHours);
      additionalDays = Math.ceil(adjustedHrDiff / 24);
    }

    return additionalDays * dailyRate;
  }

  // Default to fixed price
  return Number(service.fixedPrice) || 0;
};
